import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Dropdown, Nav, Spinner } from 'react-bootstrap';
import { Download, ExclamationTriangleFill } from 'react-bootstrap-icons';
import { ClientSidebar } from '@/components/NumaDashboard/ClientSidebar';
import { WindowSelector } from '@/components/NumaDashboard/WindowSelector';
import { README_MARKDOWN } from '@/components/NumaDashboard/tabs/DataGuide';
import { DASHBOARD_TABS } from '@/components/NumaDashboard/tabs/registry';
import type { DashboardTabProps, Selection } from '@/components/NumaDashboard/tabs/registry';
import { CurrencyProvider } from '@/components/NumaDashboard/currencyContext';
import { CurrencyControl } from '@/components/NumaDashboard/CurrencyControl';
import { scopeFleetToStack, useFleetData } from '@/components/NumaDashboard/useFleetData';
import { useClientFlags } from '@/hooks/useClientFlags';
import type { FleetTier, Freshness, SidebarStack, SidebarView } from '@/components/NumaDashboard/fleetData';
import { stackContract } from '@/components/NumaDashboard/contracts';
import type { ContractOf, StackContract } from '@/components/NumaDashboard/contracts';
import {
  accountOrgOf,
  ALL_TIERS,
  asOfDay,
  fleetViewById,
  fleetViews,
  INFRA_CLIENT_PREFIX,
  isAggregate,
  isInfraAccount,
  modelLinesDaily,
  poolForAggregate,
  resolveWindow,
  setAllData,
  setLastNDays,
  snapshotForDisplay,
  stackKind,
  stackLabel,
  tiersNeededFor,
  uniqueAccountCount,
  viewForSelection,
  viewNote,
  windowWords,
} from '@/components/NumaDashboard/shared';
import type { WindowState } from '@/components/NumaDashboard/shared';
import { computeInferredCosts } from '@/components/NumaDashboard/inferredCosts';
import { selectStackRows } from '@/components/NumaDashboard/selectors';
import { buildDashboardExport, buildDashboardWindowView } from '@/components/NumaDashboard/dashboardExport';
import { formatNzDateTime, timeAgo } from '@/utils/relativeTime';
import type { ClientSnapshot } from '@/types/fleetAnalytics';
import '@/components/NumaDashboard/styles.css';

// The page opens on what Arcanum pays AWS: the figure looked at most, and the
// one that reconciles against the AWS bill.
const DEFAULT_SELECTION = '_ARCANUM';

/** What is still on its way, in words for the line under the header. */
const TIER_WORDS: Record<FleetTier, string> = {
  users: 'per-person history',
  agents: 'per-agent history',
  services: 'spend by service',
  inventory: 'schedules, agents and integrations',
  apps: 'apps and dashboards',
  conversations: 'top conversations',
};

type ExportKind = 'selection' | 'view' | 'raw';

/** How many names a banner or a note lists before "and N more". The rest are in its hover text. */
const BANNER_NAMES = 3;

/** "49 d", "37 h": how old a stack's data is, short enough to list several. */
function ageWords(f: Freshness): string {
  if (f.ageHours === null) return 'no data';
  return f.ageHours < 48 ? `${Math.round(f.ageHours)} h` : `${Math.round(f.ageHours / 24)} d`;
}

/** One sentence on a single stack or account: what it is and where it runs. */
function describeStack(snapshot: ClientSnapshot | undefined): string {
  if (!snapshot) return 'No data is stored for this client.';
  const cfg = snapshot.client_config || {};
  if (isInfraAccount(snapshot)) {
    const account = cfg.client_account_id ? `AWS account ${cfg.client_account_id}` : 'An AWS account';
    return `${account} with no Numa stack in it. It carries AWS cost only.`;
  }
  const where = cfg.region ? ` in ${cfg.region}` : '';
  const kind = stackKind(snapshot);
  const what = kind === 'internal' ? "Arcanum's own Numa" : kind === 'dev' ? 'A dev stack' : 'A customer stack';
  const org = accountOrgOf(snapshot);
  const whose =
    org === 'standalone'
      ? "on the customer's own AWS account. Spend here is the Numa part of their bill"
      : org === 'nextgen'
        ? 'in the NextGen organisation'
        : org === 'arcanum'
          ? "in Arcanum's organisation"
          : 'with no organisation on record';
  return `${what}${where}, ${whose}.`;
}

function download(payload: string, type: string, filename: string): void {
  const url = URL.createObjectURL(new Blob([payload], { type }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** No client is left out of a view. */
const NOBODY: ReadonlySet<string> = new Set();

/**
 * The value held for `key`. A new value under the same key gives back the one
 * held before, so the sums that depend on it do not run again. The tags are
 * read again every few minutes, and most reads change nothing.
 */
function useHeldForKey<T>(key: string, value: T): T {
  const [held, setHeld] = useState({ key, value });
  if (held.key !== key) {
    setHeld({ key, value });
    return value;
  }
  return held.value;
}

/** A snapshot as the rollup wrote it: the portal's own load marker is not part of the data. */
function withoutPortalMarker(s: ClientSnapshot): ClientSnapshot {
  const copy = { ...s };
  delete copy.portal;
  return copy;
}

/**
 * Numa Dashboard: what the fleet costs and how it is used.
 *
 *   sidebar            header: what is selected, how fresh, currency, export
 *   (views, stacks)    banner: one line, only when data is old or an update failed
 *                      notes: quiet lines, only when something is unusual
 *                      time window
 *                      tabs (from tabs/registry.tsx)
 *
 * The page loads the fleet's data once (`useFleetData`), turns the sidebar
 * selection into one view (`viewForSelection`) and hands each tab that view,
 * the window and the data layer. A fleet-wide view is summed in the browser
 * from the per-client snapshots. Nothing here writes to AWS.
 */
export default function NumaDashboard() {
  const fleet = useFleetData();
  const [selected, setSelected] = useState<string>(DEFAULT_SELECTION);
  const [pickedWindow, setPickedWindow] = useState<WindowState>(() => setLastNDays(30));
  const [activeTab, setActiveTab] = useState<string>(DASHBOARD_TABS[0].key);
  const [pendingExport, setPendingExport] = useState<ExportKind | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);

  const { loading, ensureFull, ensureTier } = fleet;

  // Clients tagged Inactive are hidden from every list and from every use and
  // credit figure until somebody asks to see them. Their spend always counts.
  // The tags and contracts are read here and never written.
  const flags = useClientFlags();
  const [showInactive, setShowInactive] = useState(false);

  // Each stack's contract: the portal's live record first, the snapshot's after.
  const contractTable = useMemo(() => {
    const table = new Map<string, StackContract>();
    for (const s of fleet.snapshots) {
      table.set(s.client, stackContract(s, flags.row(s.client), flags.isInactive(s.client)));
    }
    return table;
  }, [fleet.snapshots, flags]);
  const contractKey = useMemo(
    () =>
      Array.from(contractTable, ([name, c]) =>
        [name, c.contract, c.billing, c.partner, c.inactive, c.badge?.label].join('|')
      ).join('\n'),
    [contractTable]
  );
  const freshContractOf = useMemo<ContractOf>(
    () => (s) => contractTable.get(s.client) ?? stackContract(s),
    [contractTable]
  );
  const contractOf = useHeldForKey(contractKey, freshContractOf);

  // The tags are read again every few minutes. The set below changes only
  // when the names in it change, so a refresh with the same tags sums nothing again.
  const inactiveKey = useMemo(
    () =>
      fleet.snapshots
        .filter((s) => flags.isInactive(s.client))
        .map((s) => s.client)
        .sort()
        .join('\n'),
    [fleet.snapshots, flags]
  );
  const inactiveNames = useMemo(() => new Set(inactiveKey ? inactiveKey.split('\n') : []), [inactiveKey]);
  const leftOut = showInactive ? NOBODY : inactiveNames;

  // The fixed views and one for each partner, in sidebar order.
  const freshViewDefs = useMemo(() => fleetViews(fleet.snapshots, contractOf), [fleet.snapshots, contractOf]);
  const viewDefs = useHeldForKey(freshViewDefs.map((v) => `${v.id}|${v.label}`).join('\n'), freshViewDefs);

  const viewDef = fleetViewById(selected, viewDefs);
  const singleSelection = !viewDef;

  // One stack or account: load its complete snapshot. Every tab then draws
  // from it, the people map included. The core row draws first.
  useEffect(() => {
    if (singleSelection && !loading) ensureFull(selected);
  }, [singleSelection, selected, loading, ensureFull]);

  const full = singleSelection ? fleet.fullSnapshot(selected) : undefined;
  const snapshots = useMemo(
    () => (full ? fleet.snapshots.map((s) => (s.client === selected ? full : s)) : fleet.snapshots),
    [fleet.snapshots, full, selected]
  );

  // Spend takes the model lines out of the AWS bill. A snapshot that does not
  // carry them ready-made needs its per-service breakdown, which is a tier of
  // its own: ask for it at once, so spend is on screen within seconds.
  useEffect(() => {
    if (fleet.source !== 'index') return;
    const needed = new Set<FleetTier>();
    for (const s of fleet.snapshots) {
      if (modelLinesDaily(s.cost_explorer).known) continue;
      for (const t of tiersNeededFor(s, 'cost_explorer.by_service_daily')) needed.add(t);
      if (needed.size) break;
    }
    needed.forEach(ensureTier);
  }, [fleet.source, fleet.snapshots, ensureTier]);

  const loadedTiers = useMemo(() => ALL_TIERS.filter((t) => fleet.tiers[t] === 'ready'), [fleet.tiers]);
  const tierOpts = useMemo(
    () => ({
      tiers: loadedTiers,
      tierPaths: fleet.tierPaths,
      inactive: inactiveNames,
      showInactive,
      contractOf,
      views: viewDefs,
    }),
    [loadedTiers, fleet.tierPaths, inactiveNames, showInactive, contractOf, viewDefs]
  );

  const { view, pool, costOnly } = useMemo(
    () => viewForSelection(selected, snapshots, tierOpts),
    [selected, snapshots, tierOpts]
  );

  // "All" starts on this view's own first day, so it is worked out again for each selection.
  const win = useMemo(() => resolveWindow(pickedWindow, view), [pickedWindow, view]);

  // ── Sidebar ──────────────────────────────────────────────────────────────
  const views: SidebarView[] = useMemo(
    () =>
      viewDefs.map((def) => {
        const members = poolForAggregate(fleet.snapshots, def, tierOpts);
        return {
          id: def.id,
          label: def.label,
          description: def.description,
          // The Arcanum views hold accounts with no stack, so they count accounts.
          count: def.unit === 'accounts' ? uniqueAccountCount(members) : members.length,
          unit: def.unit,
          group: def.group,
        };
      }),
    [viewDefs, fleet.snapshots, tierOpts]
  );

  const stackRows = useMemo(
    () => selectStackRows(snapshots, win, fleet.freshness, { contractOf, inactive: inactiveNames }),
    [snapshots, win, fleet.freshness, contractOf, inactiveNames]
  );
  const stacks: SidebarStack[] = useMemo(() => {
    // An inactive client stays in the list while it is the one on screen.
    const listed = stackRows.rows.filter((r) => !leftOut.has(r.client) || r.client === selected);
    const rows: SidebarStack[] = listed.map((r) => ({
      name: r.client,
      inactive: inactiveNames.has(r.client),
      inactiveTag: flags.inactive(r.client),
      contract: r.contract
        ? { label: r.contract, variant: r.contractVariant ?? 'secondary', hint: r.contractHint }
        : null,
      label: r.label,
      kind: r.kind,
      spendUsd: r.spend,
      conversations: r.kind === 'infra' ? null : r.conversations,
      generatedAt: r.generatedAt,
      stale: r.stale,
      failed: r.failed,
      error: r.error,
    }));
    // A client the nightly update has never worked for has no numbers, and
    // still belongs in the list: it is the one that most needs a look.
    for (const name of fleet.clientsWithNoData) {
      const f = fleet.freshness(name);
      rows.push({
        name,
        label: name,
        kind: 'customer',
        spendUsd: null,
        conversations: null,
        generatedAt: f.generatedAt,
        stale: false,
        failed: true,
        error: f.error,
      });
    }
    return rows;
  }, [stackRows, fleet, flags, leftOut, inactiveNames, selected]);

  // ── What is selected, for the header and the tabs ────────────────────────
  const selection: Selection = useMemo(() => {
    const stale = fleet.staleAmong(pool.map((s) => s.client));
    if (viewDef) {
      const newest = view && isAggregate(view) ? view.newest_generated_at : undefined;
      return {
        id: selected,
        kind: 'view',
        label: viewDef.label,
        description: viewDef.description,
        stacks: pool,
        costOnly,
        contractOf,
        inactive: inactiveNames,
        stale,
        asOf: asOfDay({ generated_at: newest }),
        viewKind: viewDef.kind,
      };
    }
    const snapshot = pool[0];
    const account = snapshot ? isInfraAccount(snapshot) : selected.startsWith(INFRA_CLIENT_PREFIX);
    const own = fleet.freshness(selected);
    return {
      id: selected,
      kind: account ? 'account' : 'stack',
      label: snapshot ? stackLabel(snapshot) : selected.replace(INFRA_CLIENT_PREFIX, ''),
      description: describeStack(snapshot),
      stacks: pool,
      costOnly,
      contractOf,
      inactive: inactiveNames,
      contract: snapshot && !account ? contractOf(snapshot) : undefined,
      stale: stale.length ? stale : own.failed ? [{ clientName: selected, ...own }] : [],
      asOf: asOfDay(snapshot),
    };
  }, [fleet, pool, costOnly, contractOf, inactiveNames, selected, view, viewDef]);

  const scopedFleet = useMemo(
    () => (singleSelection ? scopeFleetToStack(fleet, selected) : fleet),
    [fleet, selected, singleSelection]
  );

  const visibleTabs = useMemo(() => DASHBOARD_TABS.filter((t) => t.show(selection)), [selection]);
  // A tab that does not exist for this selection gives way to the first that does.
  const tab = visibleTabs.find((t) => t.key === activeTab) ?? visibleTabs[0];

  const onSelectClient = useCallback((id: string | null) => setSelected(id ?? DEFAULT_SELECTION), []);

  // ── Freshness ────────────────────────────────────────────────────────────
  const freshLine = useMemo(() => {
    if (selection.kind === 'view') {
      const newest = view && isAggregate(view) ? view.newest_generated_at : undefined;
      return newest ? { text: `Updated ${timeAgo(newest)}`, exact: formatNzDateTime(newest) } : null;
    }
    const at = fleet.freshness(selected).generatedAt;
    return at ? { text: `Updated ${timeAgo(at)}`, exact: formatNzDateTime(at) } : null;
  }, [selection.kind, view, fleet, selected]);

  /** The banner: one line, shown only when data is old or an update failed. `title` holds every name. */
  const banner = useMemo((): { text: string; title?: string } | null => {
    if (selection.kind !== 'view') {
      const own = selection.stale[0];
      if (!own) return null;
      const noun = selection.kind === 'account' ? 'account' : 'client';
      const when =
        own.ageHours === null
          ? `No data is stored for this ${noun}.`
          : `This ${noun}'s data was last updated ${timeAgo(own.generatedAt)}. Every number on this page is as of then.`;
      const why = own.failed ? ` The nightly update is failing${own.error ? `: ${own.error}` : '.'}` : '';
      return { text: when + why };
    }
    const stale = selection.stale;
    if (!stale.length) return null;
    const bySelection = new Map(selection.stacks.map((s) => [s.client, s]));
    const accounts = stale.filter((f) => {
      const s = bySelection.get(f.clientName);
      return s ? isInfraAccount(s) : false;
    }).length;
    const one = stale.length === 1;
    const noun =
      accounts === stale.length
        ? one
          ? 'account'
          : 'accounts'
        : accounts === 0
          ? one
            ? 'stack'
            : 'stacks'
          : 'stacks and accounts';
    const named = stale.map((f) => {
      const s = bySelection.get(f.clientName);
      return `${s ? stackLabel(s) : f.clientName} ${ageWords(f)}`;
    });
    const more = named.length > BANNER_NAMES ? ` and ${named.length - BANNER_NAMES} more` : '';
    return {
      text: `${stale.length} ${noun} in this view ${one ? 'has' : 'have'} old data: ${named.slice(0, BANNER_NAMES).join(', ')}${more}. ${one ? 'Its' : 'Their'} numbers are in the totals as of the last update.`,
      title: named.length > BANNER_NAMES ? named.join(', ') : undefined,
    };
  }, [selection]);

  /** Quiet lines under the header: what is unusual about what is on screen. Most days there are none. */
  const notes = useMemo(() => {
    const lines: Array<{ text: string; title?: string }> = [];
    if (loading) return lines;
    if (viewDef) {
      const exception = viewNote(viewDef, fleet.snapshots);
      if (exception) lines.push({ text: exception });
      // Clients with no data at all are in no organisation on record, so they
      // are named on the two views a reader expects them in.
      const none = fleet.clientsWithNoData;
      if (none.length && (viewDef.kind === 'fleet' || viewDef.kind === 'clients')) {
        const more = none.length > BANNER_NAMES ? ` and ${none.length - BANNER_NAMES} more` : '';
        lines.push({
          text: `${none.length} ${none.length === 1 ? 'client has' : 'clients have'} no data and ${none.length === 1 ? 'is' : 'are'} in no total: ${none.slice(0, BANNER_NAMES).join(', ')}${more}.`,
          title: none.length > BANNER_NAMES ? none.join(', ') : undefined,
        });
      }
    }
    if (fleet.source === 'dynamodb') {
      lines.push({
        text: 'Read from the older per-client records. Per-person and per-agent history goes back 92 days.',
      });
    }
    return lines;
  }, [loading, viewDef, fleet.snapshots, fleet.clientsWithNoData, fleet.source]);

  const loadingMore = useMemo(() => {
    if (singleSelection) return fleet.fullLoading(selected) ? ["this client's full history"] : [];
    return ALL_TIERS.filter((t) => fleet.tiers[t] === 'loading').map((t) => TIER_WORDS[t]);
  }, [fleet, selected, singleSelection]);

  // ── Exports ──────────────────────────────────────────────────────────────
  // An export holds everything, so it waits for the data a tab has not asked
  // for yet: every tier, or one stack's complete snapshot.
  const exportReady = useCallback(
    (kind: ExportKind): boolean => {
      if (fleet.loading) return false;
      if (fleet.source !== 'index') return true;
      if (kind === 'selection' && singleSelection) {
        return Boolean(fleet.fullSnapshot(selected)) || fleet.fullFailed(selected);
      }
      return ALL_TIERS.every((t) => fleet.tiers[t] === 'ready' || fleet.tiers[t] === 'error');
    },
    [fleet, selected, singleSelection]
  );

  const runExport = useCallback(
    (kind: ExportKind) => {
      const stamp = new Date().toISOString().slice(0, 10);
      const save = (payload: unknown, suffix: string) =>
        download(JSON.stringify(payload, null, 2), 'application/json', `numa-dashboard-${suffix}-${stamp}.json`);
      const missing = ALL_TIERS.filter((t) => fleet.tiers[t] === 'error');

      if (kind === 'raw') {
        // Every snapshot as the rollup wrote it, plus spend worked out for
        // each over its own history. Spend is worked out on the bill as the
        // dashboard shows it: a standalone customer's is cut to the Numa part.
        const raw: Record<string, ClientSnapshot> = {};
        const inferredCosts: Record<string, ReturnType<typeof computeInferredCosts>> = {};
        for (const s of snapshots) {
          raw[s.client] = withoutPortalMarker(s);
          const display = snapshotForDisplay(s);
          const all = setAllData(display);
          if (all) inferredCosts[s.client] = computeInferredCosts(display, all);
        }
        save(
          {
            exported_at: new Date().toISOString(),
            export_kind: 'raw_snapshots' as const,
            snapshot_count: snapshots.length,
            source: fleet.source,
            index_generated_at: fleet.indexGeneratedAt,
            tiers_not_loaded: missing,
            note:
              fleet.source === 'dynamodb'
                ? 'Read from the old per-client rows: per-person, per-agent and per-service daily maps hold the last 92 days only.'
                : 'Complete snapshots, joined from the tier files. A standalone customer keeps its whole AWS bill here; inferred_costs is worked out on the Numa part.',
            snapshots: raw,
            inferred_costs: inferredCosts,
          },
          'raw'
        );
        return;
      }

      if (kind === 'view') {
        save({ export_kind: 'dashboard_view' as const, ...buildDashboardExport(snapshots, tierOpts) }, 'view');
        return;
      }

      if (!view) return;
      const windows = [7, 14, 30, 60, 90].map((n) => setLastNDays(n));
      const all = setAllData(view);
      if (all) windows.push(all);
      if (!windows.some((w) => w.startDate === win.startDate && w.endDate === win.endDate)) windows.push(win);
      save(
        {
          export_kind: 'single_selection' as const,
          exported_at: new Date().toISOString(),
          selection: selected,
          selection_label: selection.label,
          is_aggregate: isAggregate(view),
          stale_stacks: selection.stale.map((f) => ({
            client: f.clientName,
            generated_at: f.generatedAt,
            failed: f.failed,
          })),
          snapshot: isAggregate(view) ? undefined : withoutPortalMarker(pool[0]),
          inferred_costs: computeInferredCosts(view, win),
          window_views: Object.fromEntries(
            windows.map((w) => [w.label, buildDashboardWindowView(view, w, snapshots, contractOf)])
          ),
        },
        selected.replace(/[^a-zA-Z0-9_-]/g, '') || 'selection'
      );
    },
    [fleet, contractOf, pool, selected, selection, snapshots, tierOpts, view, win]
  );

  const startExport = useCallback(
    (kind: ExportKind) => {
      setExportError(null);
      if (fleet.source === 'index') {
        if (kind === 'selection' && singleSelection) fleet.ensureFull(selected);
        else ALL_TIERS.forEach(fleet.ensureTier);
      }
      setPendingExport(kind);
    },
    [fleet, selected, singleSelection]
  );

  useEffect(() => {
    if (!pendingExport || !exportReady(pendingExport)) return;
    try {
      runExport(pendingExport);
    } catch (e) {
      setExportError(`The export did not finish: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setPendingExport(null);
    }
  }, [pendingExport, exportReady, runExport]);

  const downloadReadme = useCallback(() => {
    download(README_MARKDOWN, 'text/markdown', `numa-dashboard-README-${new Date().toISOString().slice(0, 10)}.md`);
  }, []);

  const tabProps: DashboardTabProps | null = view
    ? {
        data: view,
        window: win,
        snapshots,
        fleet: scopedFleet,
        wholeFleet: fleet,
        selection,
        onSelectClient,
        onSelectTab: setActiveTab,
      }
    : null;

  return (
    <CurrencyProvider>
      <div className="nd-shell">
        <ClientSidebar
          loading={loading}
          views={views}
          stacks={stacks}
          selected={selected}
          onSelect={setSelected}
          windowLabel={windowWords(win)}
          inactiveCount={inactiveNames.size}
          showInactive={showInactive}
          onShowInactive={setShowInactive}
        />

        <div className="nd-main">
          {/* The same compact title row as every page: the name, one line of fact, the page's controls. */}
          <header className="page-header nd-head">
            <div className="page-header__text nd-head__text">
              <h1 className="page-header__title">{selection.label}</h1>
              <div className="page-header__description nd-head__facts">
                <span>{selection.description}</span>
                {loading && <span>Loading fleet data</span>}
                {!loading && freshLine && <span title={freshLine.exact}>{freshLine.text}</span>}
                {!loading && loadingMore.length > 0 && (
                  <span className="nd-head__loading">
                    <Spinner animation="border" size="sm" />
                    Loading {loadingMore.join(', ')}
                  </span>
                )}
              </div>
            </div>

            <div className="page-header__actions nd-head__actions">
              <CurrencyControl />
              <Dropdown align="end">
                <Dropdown.Toggle variant="outline-secondary" size="sm" disabled={loading || pendingExport !== null}>
                  {pendingExport ? <Spinner animation="border" size="sm" /> : <Download />}
                  <span className="nd-head__export-label">{pendingExport ? 'Getting the data' : 'Export'}</span>
                </Dropdown.Toggle>
                <Dropdown.Menu className="nd-export-menu">
                  <Dropdown.Item onClick={() => startExport('selection')} disabled={!view}>
                    <span className="nd-export-menu__name">This selection (JSON)</span>
                    <span className="nd-export-menu__hint">
                      {selection.label}: its data, and its figures for each time window.
                    </span>
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => startExport('view')}>
                    <span className="nd-export-menu__name">Every stack, as on screen (JSON)</span>
                    <span className="nd-export-menu__hint">
                      The figures for each stack and each fleet view, for 7, 14, 30, 60 and 90 days.
                    </span>
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => startExport('raw')}>
                    <span className="nd-export-menu__name">Raw snapshots (JSON)</span>
                    <span className="nd-export-menu__hint">
                      Every stack&apos;s full data with all daily series. Holds people&apos;s email addresses.
                    </span>
                  </Dropdown.Item>
                  <Dropdown.Divider />
                  <Dropdown.Item onClick={downloadReadme}>
                    <span className="nd-export-menu__name">Data guide (README)</span>
                    <span className="nd-export-menu__hint">
                      What each field means. Give it to an AI model together with an export.
                    </span>
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </div>
          </header>

          {fleet.error && (
            <Alert variant="danger" className="nd-alert">
              <span>{fleet.error}</span>
              <Button variant="outline-danger" size="sm" onClick={fleet.reload}>
                Try again
              </Button>
            </Alert>
          )}
          {exportError && (
            <Alert variant="danger" className="nd-alert" dismissible onClose={() => setExportError(null)}>
              {exportError}
            </Alert>
          )}

          {!loading && banner && (
            <div className="nd-banner" role="status" title={banner.title}>
              <ExclamationTriangleFill className="nd-banner__icon" aria-hidden="true" />
              <p>{banner.text}</p>
            </div>
          )}
          {notes.length > 0 && (
            <div className="nd-notes">
              {notes.map((line) => (
                <p key={line.text} title={line.title}>
                  {line.text}
                </p>
              ))}
            </div>
          )}

          <div className="nd-body nd-root">
            {loading && !view && (
              <div className="nd-state">
                <Spinner animation="border" size="sm" />
                Loading fleet data
              </div>
            )}
            {!loading && !view && !fleet.error && (
              <div className="nd-state">
                No data for {selection.label}. The nightly update fills this page once it has run for the client.
              </div>
            )}

            {view && tabProps && tab && (
              <>
                <WindowSelector data={view} value={win} onChange={setPickedWindow} />

                <Nav
                  variant="tabs"
                  className="nd-tabbar"
                  activeKey={tab.key}
                  onSelect={(key) => key && setActiveTab(key)}
                >
                  {visibleTabs.map((t) => (
                    <Nav.Item key={t.key}>
                      <Nav.Link eventKey={t.key}>{t.label}</Nav.Link>
                    </Nav.Item>
                  ))}
                </Nav>

                <div className="nd-tabpanel" key={tab.key}>
                  {tab.render(tabProps)}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </CurrencyProvider>
  );
}
